import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  PaymentStep,
  useStepContext,
} from '~/app/providers/paymentStepProvider';
import { formatAmount } from '~/shared/lib/formatAmount';
import { generateGradient } from '~/shared/lib/generateGradient';
import { initCountryLocales } from '~/shared/lib/initCountryLocales';
import { CurrencyCode, MonetaryAmount } from '~/shared/types';
import { Divider } from '~/shared/ui/Divider/Divider';
import { Halo } from '~/shared/ui/Halo/Halo.ui';
import { Header } from '~/shared/ui/Header/Header';
import { Icon } from '~/shared/ui/Icon/Icon';
import { IconImage } from '~/shared/ui/IconLogo/IconLogo.ui';

import { useGetCatalogPrices } from '../../../../hooks/useGetCatalogPrices';
import { useLocalization } from '../../../../localization/localization';
import { FormType } from '../../../PayPage.scheme';
import { Logo } from '../../Logo/Logo';
import { Products } from '../../Products/Products';
import { SbpButton } from '../../SbpButton/SbpButton';
import { CERTIFICATES_CONFIG } from '../SelectCertificateStep/constants/certificates';

import {
  BottomContent,
  ButtonLabel,
  Content,
  Main,
  Root,
  StyledAccordion,
} from './SelectProductStep.ui';

export const SelectProductStep = () => {
  const { t } = useLocalization();
  const { getValues, watch, setValue } = useFormContext<FormType>();
  const currentCertificate = getValues('certificate');
  const { product, amount } = watch();
  const { color, name, brand } = currentCertificate || {};
  const { i18n } = useTranslation();
  const { goToStep, registerStepCleanup } = useStepContext();
  initCountryLocales(i18n.language);

  useEffect(() => {
    return registerStepCleanup(PaymentStep.SELECT_PRODUCT, () => {
      setValue('product', undefined);
      setValue('amount', undefined);
    });
  }, [registerStepCleanup, setValue]);

  const certificateData = CERTIFICATES_CONFIG[brand?.slug || ''];

  const { isLoading: isPricesLoading, error: isPricesError } =
    useGetCatalogPrices({
      productId: product?.id || '',
    });

  const isZeroPrice = Number(amount?.majorValue) === 0;
  const isError = isPricesError || isZeroPrice;

  const accordionData = [
    {
      title: t('how to activate title'),
      desc: t('how to activate desc'),
    },
  ];

  return (
    <Root>
      <Header
        leftIconSlot={
          certificateData?.logoUrl ? (
            <IconImage
              alt={name}
              loading="lazy"
              size={24}
              src={certificateData.logoUrl}
            />
          ) : (
            <Logo name={name || ''} />
          )
        }
        title={name || ''}
        type="back"
      />
      <Halo
        aria-hidden="true"
        gradient={color && generateGradient(color)}
        top={300}
      />
      <Main>
        <Content isExpanded={!!product}>
          <Divider label={t('Select certificate')} />
          <Products
            isZeroPrice={isZeroPrice}
            isPricesError={!!isPricesError}
            products={(currentCertificate?.products || []) as any}
          />
          <StyledAccordion items={accordionData} />
        </Content>
        {(isPricesLoading || !!amount || isError) && (
          <BottomContent>
            <SbpButton
              isLoading={isPricesLoading}
              disabled={!!isError || isPricesLoading}
              type="button"
              onClick={() => {
                goToStep(PaymentStep.PAYMENT);
              }}
            >
              <ButtonLabel>
                {!!isError
                  ? t('price error button')
                  : amount &&
                    formatAmount({
                      amount: amount.majorValue as MonetaryAmount,
                      currency: amount.currency as CurrencyCode,
                    })}{' '}
                {!isError && <Icon name="arrow-right" width={24} />}
              </ButtonLabel>
            </SbpButton>
          </BottomContent>
        )}
      </Main>
    </Root>
  );
};
